import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Plus, Edit2, Trash2, Save, X, Loader2, 
  Video, FileText, Clock, DollarSign, ChevronDown, ChevronUp,
  GripVertical, BookOpen, Eye, EyeOff, Palette
} from 'lucide-react';
import Header from '../components/Header';
import '../styles/Admin.css';


const Admin = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showNewCourseForm, setShowNewCourseForm] = useState(false);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/login');
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchCourses();
    }
  }, [user]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/courses`, {
        withCredentials: true
      });
      setCourses(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async (courseId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/courses/${courseId}/modules`, {
        withCredentials: true
      });
      setModules(prev => ({ ...prev, [courseId]: response.data }));
    } catch (err) {
      console.error('Failed to load modules:', err);
    }
  };

  const handleExpandCourse = async (courseId) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
    } else {
      setExpandedCourse(courseId);
      if (!modules[courseId]) {
        await fetchModules(courseId);
      }
    }
  };

  // Course Management Functions
  const handleCreateCourse = async (courseData) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/courses`, courseData, {
        withCredentials: true
      });
      setCourses([...courses, response.data]);
      setShowNewCourseForm(false);
    } catch (err) {
      console.error('Failed to create course:', err);
      alert(err.response?.data?.detail || 'Failed to create course');
    }
  };

  const handleUpdateCourse = async (courseId, updates) => {
    try {
      const response = await axios.put(`${API_URL}/api/admin/courses/${courseId}`, updates, {
        withCredentials: true
      });
      setCourses(courses.map(c => c.id === courseId ? response.data : c));
      setEditingCourse(null);
    } catch (err) {
      console.error('Failed to update course:', err);
      alert(err.response?.data?.detail || 'Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This will also delete all modules.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/admin/courses/${courseId}`, {
        withCredentials: true
      });
      setCourses(courses.filter(c => c.id !== courseId));
      if (expandedCourse === courseId) {
        setExpandedCourse(null);
      }
    } catch (err) {
      console.error('Failed to delete course:', err);
      alert(err.response?.data?.detail || 'Failed to delete course');
    }
  };

  // Module Management Functions
  const handleCreateModule = async (courseId) => {
    const courseModules = modules[courseId] || [];
    const newModule = {
      course_id: courseId,
      title: 'New Module',
      order: courseModules.length + 1,
      video_url: '',
      duration_minutes: 5,
      notes_markdown: '# Module Title\n\nAdd your notes here...'
    };
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/courses/${courseId}/modules`,
        newModule,
        { withCredentials: true }
      );
      setModules(prev => ({
        ...prev,
        [courseId]: [...(prev[courseId] || []), response.data]
      }));
      setEditingModule(response.data.id);
    } catch (err) {
      console.error('Failed to create module:', err);
    }
  };

  const handleUpdateModule = async (moduleId, courseId, updates) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/modules/${moduleId}`,
        updates,
        { withCredentials: true }
      );
      setModules(prev => ({
        ...prev,
        [courseId]: prev[courseId].map(m => m.id === moduleId ? response.data : m)
      }));
      setEditingModule(null);
    } catch (err) {
      console.error('Failed to update module:', err);
    }
  };

  const handleDeleteModule = async (moduleId, courseId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/modules/${moduleId}`, {
        withCredentials: true
      });
      setModules(prev => ({
        ...prev,
        [courseId]: prev[courseId].filter(m => m.id !== moduleId)
      }));
    } catch (err) {
      console.error('Failed to delete module:', err);
    }
  };

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(cents / 100);
  };

  if (authLoading || loading) {
    return (
      <div className="admin-page">
        <Header />
        <div className="admin-loading">
          <Loader2 size={48} className="spinner" />
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="admin-page">
      <Header />
      
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1>
              <Shield size={32} />
              Course Administration
            </h1>
            <p>Manage your courses and modules</p>
          </div>
          <div className="admin-header-actions">
            <Link to="/visual-editor" className="btn-visual-editor">
              <Palette size={20} />
              Visual Editor
            </Link>
            <button 
              className="btn-add-course"
              onClick={() => setShowNewCourseForm(true)}
            >
              <Plus size={20} />
              Add New Course
            </button>
          </div>
        </div>

        {error && (
          <div className="admin-error">
            <p>{error}</p>
          </div>
        )}

        {/* New Course Form */}
        {showNewCourseForm && (
          <CourseForm
            onSave={handleCreateCourse}
            onCancel={() => setShowNewCourseForm(false)}
          />
        )}

        <div className="courses-admin-list">
          <h2>Your Courses ({courses.length})</h2>
          
          {courses.map((course) => (
            <div key={course.id} className="course-admin-card">
              {editingCourse === course.id ? (
                <CourseForm
                  course={course}
                  onSave={(updates) => handleUpdateCourse(course.id, updates)}
                  onCancel={() => setEditingCourse(null)}
                  isEditing
                />
              ) : (
                <>
                  <div className="course-admin-header">
                    <div 
                      className="course-admin-info"
                      onClick={() => handleExpandCourse(course.id)}
                    >
                      <h3>{course.title}</h3>
                      <div className="course-admin-meta">
                        <span className={`status-badge ${course.status}`}>
                          {course.status === 'live' ? <Eye size={12} /> : <EyeOff size={12} />}
                          {course.status}
                        </span>
                        <span>
                          <DollarSign size={14} />
                          {formatPrice(course.price_cents)}
                        </span>
                        <span>
                          <Clock size={14} />
                          {course.duration_minutes || 0} min
                        </span>
                      </div>
                      <p className="course-slug">/{course.slug}</p>
                    </div>
                    <div className="course-actions">
                      <button 
                        className="btn-edit-course"
                        onClick={() => setEditingCourse(course.id)}
                        title="Edit Course"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        className="btn-delete-course"
                        onClick={() => handleDeleteCourse(course.id, course.title)}
                        title="Delete Course"
                      >
                        <Trash2 size={18} />
                      </button>
                      <button 
                        className="expand-btn"
                        onClick={() => handleExpandCourse(course.id)}
                      >
                        {expandedCourse === course.id ? (
                          <ChevronUp size={24} />
                        ) : (
                          <ChevronDown size={24} />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {expandedCourse === course.id && (
                    <div className="course-modules-section">
                      <div className="modules-header">
                        <h4>
                          <BookOpen size={18} />
                          Modules ({modules[course.id]?.length || 0})
                        </h4>
                        <button 
                          className="btn-add-module"
                          onClick={() => handleCreateModule(course.id)}
                        >
                          <Plus size={18} />
                          Add Module
                        </button>
                      </div>
                      
                      {!modules[course.id] || modules[course.id].length === 0 ? (
                        <div className="no-modules">
                          <p>No modules yet. Add your first module to get started.</p>
                        </div>
                      ) : (
                        <div className="modules-list">
                          {modules[course.id].map((module) => (
                            <ModuleEditor
                              key={module.id}
                              module={module}
                              courseId={course.id}
                              isEditing={editingModule === module.id}
                              onEdit={() => setEditingModule(module.id)}
                              onSave={(updates) => handleUpdateModule(module.id, course.id, updates)}
                              onCancel={() => setEditingModule(null)}
                              onDelete={() => handleDeleteModule(module.id, course.id)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          
          {courses.length === 0 && (
            <div className="no-courses">
              <BookOpen size={48} />
              <p>No courses yet. Create your first course to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Course Form Component
const CourseForm = ({ course, onSave, onCancel, isEditing }) => {
  const [form, setForm] = useState({
    title: course?.title || '',
    slug: course?.slug || '',
    short_description: course?.short_description || '',
    long_description: course?.long_description || '',
    price_cents: course?.price_cents || 4900,
    currency: course?.currency || 'AUD',
    status: course?.status || 'draft',
    thumbnail_url: course?.thumbnail_url || ''
  });
  const [priceDisplay, setPriceDisplay] = useState((course?.price_cents || 4900) / 100);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handlePriceChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setPriceDisplay(numValue);
    setForm({ ...form, price_cents: Math.round(numValue * 100) });
  };

  const handleTitleChange = (title) => {
    setForm({
      ...form,
      title,
      slug: isEditing ? form.slug : title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    });
  };

  const handleSubmit = () => {
    if (!form.title.trim()) {
      alert('Please enter a course title');
      return;
    }
    if (!form.slug.trim()) {
      alert('Please enter a course slug');
      return;
    }
    onSave(form);
  };

  return (
    <div className="course-form">
      <h3>{isEditing ? 'Edit Course' : 'Create New Course'}</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Course Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="e.g., Secure Your Social Media"
          />
        </div>
      </div>
      
      <div className="form-row two-col">
        <div className="form-group">
          <label>URL Slug</label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="cyber-privacy-security-basics"
          />
          <span className="form-hint">URL will be: /courses/{form.slug || 'your-slug'}</span>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            <option value="draft">Draft (Hidden)</option>
            <option value="live">Live (Published)</option>
          </select>
        </div>
      </div>
      
      <div className="form-row two-col">
        <div className="form-group">
          <label>
            <DollarSign size={16} />
            Price (AUD)
          </label>
          <input
            type="number"
            value={priceDisplay}
            onChange={(e) => handlePriceChange(e.target.value)}
            min="0"
            step="0.01"
          />
        </div>
        <div className="form-group">
          <label>Thumbnail URL (optional)</label>
          <input
            type="text"
            value={form.thumbnail_url}
            onChange={(e) => handleChange('thumbnail_url', e.target.value)}
            placeholder="https://..."
          />
        </div>
      </div>
      
      <div className="form-group">
        <label>Short Description</label>
        <textarea
          value={form.short_description}
          onChange={(e) => handleChange('short_description', e.target.value)}
          placeholder="Brief description shown on course cards"
          rows={2}
        />
      </div>
      
      <div className="form-group">
        <label>Full Description</label>
        <textarea
          value={form.long_description}
          onChange={(e) => handleChange('long_description', e.target.value)}
          placeholder="Detailed description shown on course detail page"
          rows={4}
        />
      </div>
      
      <div className="form-actions">
        <button className="btn-save" onClick={handleSubmit}>
          <Save size={18} />
          {isEditing ? 'Save Changes' : 'Create Course'}
        </button>
        <button className="btn-cancel" onClick={onCancel}>
          <X size={18} />
          Cancel
        </button>
      </div>
    </div>
  );
};

// Module Editor Component
const ModuleEditor = ({ module, courseId, isEditing, onEdit, onSave, onCancel, onDelete }) => {
  const [form, setForm] = useState({
    title: module.title,
    order: module.order,
    video_url: module.video_url || '',
    duration_minutes: module.duration_minutes || 0,
    notes_markdown: module.notes_markdown || ''
  });

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  // Helper to get video embed URL (YouTube or Vimeo)
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // Vimeo URL patterns
    const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return {
        type: 'vimeo',
        url: `https://player.vimeo.com/video/${vimeoMatch[1]}?title=0&byline=0&portrait=0`
      };
    }
    
    // YouTube URL patterns
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0&modestbranding=1`
      };
    }
    
    return null;
  };

  if (isEditing) {
    return (
      <div className="module-card editing">
        <div className="module-form">
          <div className="form-row three-col">
            <div className="form-group flex-grow">
              <label>Module Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter module title"
              />
            </div>
            <div className="form-group small">
              <label>Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="form-group small">
              <label>Duration (min)</label>
              <input
                type="number"
                value={form.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
                min="0"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>
              <Video size={16} />
              Video URL (Vimeo or YouTube)
            </label>
            <input
              type="text"
              value={form.video_url}
              onChange={(e) => handleChange('video_url', e.target.value)}
              placeholder="https://vimeo.com/123456789 or https://www.youtube.com/watch?v=..."
            />
            <span className="form-hint">
              <strong>Recommended: Use Vimeo</strong> for secure video hosting. Go to your Vimeo video, click Share, and paste the URL here.
              <br />
              <em>Tip: Set domain restrictions in Vimeo to only allow embedding on your site.</em>
            </span>
            
            {form.video_url && getVideoEmbedUrl(form.video_url) && (
              <div className="video-preview">
                <iframe
                  src={getVideoEmbedUrl(form.video_url).url}
                  title="Video Preview"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label>
              <FileText size={16} />
              Module Notes (Markdown)
            </label>
            <textarea
              value={form.notes_markdown}
              onChange={(e) => handleChange('notes_markdown', e.target.value)}
              placeholder="# Module Title&#10;&#10;Your notes in markdown format..."
              rows={8}
            />
            <span className="form-hint">
              These notes will be displayed below the video for students
            </span>
          </div>
          
          <div className="module-actions">
            <button className="btn-save" onClick={() => onSave(form)}>
              <Save size={18} />
              Save Module
            </button>
            <button className="btn-cancel" onClick={onCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="module-card">
      <div className="module-grip">
        <GripVertical size={18} />
      </div>
      <div className="module-info">
        <span className="module-order">{module.order}</span>
        <div className="module-details">
          <h5>{module.title}</h5>
          <div className="module-meta">
            {module.video_url ? (
              <span className="has-video">
                <Video size={14} />
                Video added
              </span>
            ) : (
              <span className="no-video">
                <Video size={14} />
                No video
              </span>
            )}
            <span>
              <Clock size={14} />
              {module.duration_minutes} min
            </span>
          </div>
        </div>
      </div>
      <div className="module-actions">
        <button className="btn-edit" onClick={onEdit}>
          <Edit2 size={16} />
        </button>
        <button className="btn-delete" onClick={onDelete}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default Admin;
